import { useState } from 'react';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { useCreateIpdBedTransfer, useIpdBedTransfers } from '../ipd-daycare/ipd-daycare.queries';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { useDialysisLookup } from './dialysis.queries';
import { FieldLabel, LookupSelect } from './dialysis.shared';
import type { DialysisAdmission } from './types';

export const DialysisBedTransferPanel = ({ admission }: { admission: DialysisAdmission }) => {
  const { showToast } = useToast();
  const query = useIpdBedTransfers(admission.id);
  const wards = useDialysisLookup('wards');
  const [ward, setWard] = useState('');
  const beds = useDialysisLookup('beds', '', { ward });
  const [bed, setBed] = useState('');
  const [reason, setReason] = useState('');
  const mutation = useCreateIpdBedTransfer(admission.id);

  const chooseWard = (value: string) => { setWard(value); setBed(''); };

  const submit = async () => {
    if (!ward || !bed) return;
    if (!(await confirmUpdate(`the bed assignment for ${admission.patientSnapshot.name}`))) return;
    mutation.mutate({ ward, bed, reason: reason.trim() || null }, {
      onSuccess: () => { setWard(''); setBed(''); setReason(''); showToast('Bed transferred successfully.', 'success'); },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The bed transfer could not be completed.', 'error'),
    });
  };

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>Bed Transfer — {admission.patientSnapshot.name}</span></div>
        <p>Currently in {admission.bedCategory.name} · Bed {admission.bed.name}</p>
      </header>

      {admission.status !== 'admitted' ? (
        <section className="opd-bill-subsection">
          <p className="opd-bill-empty-note">This patient has been discharged, so their bed can no longer be transferred.</p>
        </section>
      ) : (
        <section className="opd-bill-subsection">
          <h3>Transfer to a new bed</h3>
          <div className="ipd-daycare-admission-row">
            <label><FieldLabel required>New Bed Category</FieldLabel><LookupSelect id="dialysis-bed-transfer-ward" value={ward} options={wards.data} placeholder="Select Ward" clearable={false} onChange={chooseWard} /></label>
            <label><FieldLabel required>New Bed</FieldLabel><LookupSelect id="dialysis-bed-transfer-bed" value={bed} options={beds.data} placeholder={ward ? 'Select Bed' : 'Select Bed Category first'} disabled={!ward} onChange={setBed} /></label>
            <label><FieldLabel>Reason</FieldLabel><input value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          </div>
          {ward && beds.isSuccess && beds.data.length === 0 ? (
            <p className="opd-bill-empty-note">No vacant beds in the selected bed category — occupied beds are hidden here. Add beds in Settings → Bed &amp; Ward or free up an existing bed first.</p>
          ) : null}
          <div className="opd-form-actions">
            <button className="button button-primary" type="button" disabled={!ward || !bed || mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? 'Transferring…' : 'Transfer Bed'}</button>
          </div>
        </section>
      )}

      <section className="opd-bill-subsection">
        <h3>Bed History</h3>
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load bed history" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess ? (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><th>Ward</th><th>Bed</th><th>Duration</th><th>Reason</th><th>Transferred By</th></tr></thead>
              <tbody>
                <tr>
                  <td>{query.data.length + 1}</td>
                  <td>{admission.bedCategory.name}</td>
                  <td>{admission.bed.name}</td>
                  <td>{query.data[0] ? formatIpdDateTime(query.data[0].transferredAt) : formatIpdDateTime(admission.admissionDate)} — <strong>Present</strong></td>
                  <td>-</td>
                  <td>-</td>
                </tr>
                {query.data.map((transfer, index) => (
                  <tr key={transfer.id}>
                    <td>{query.data.length - index}</td>
                    <td>{transfer.fromWard.name}</td>
                    <td>{transfer.fromBed.name}</td>
                    <td>{formatIpdDateTime(transfer.transferredAt)}</td>
                    <td>{transfer.reason ?? '-'}</td>
                    <td>{transfer.transferredBy.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
};
